import { apiRequest } from './client';

/** Contract management (SRS #85). */

const TOKEN_KEY = 'policyora_token';
const opts = (extra: RequestInit = {}): RequestInit => {
  const token = localStorage.getItem(TOKEN_KEY);
  return { ...extra, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra.headers || {}) } };
};

export type ContractType = 'order_form' | 'msa' | 'dpa' | 'sla' | 'nda' | 'amendment' | 'other';
export type ContractStatus = 'draft' | 'active' | 'expired' | 'terminated';
export const CONTRACT_TYPE_LABEL: Record<ContractType, string> = {
  order_form: 'Order form', msa: 'Master services agreement', dpa: 'Data processing agreement', sla: 'Service level agreement',
  nda: 'Non-disclosure agreement', amendment: 'Amendment', other: 'Other',
};

export interface Contract {
  id: number; user_id: number; reference: string; title: string; contract_type: ContractType; status: ContractStatus;
  counterparty: string | null; start_date: string | null; end_date: string | null; days_to_end: number | null;
  auto_renew: boolean; renewal_term_months: number | null; notice_period_days: number | null;
  notice_deadline: string | null; days_to_notice_deadline: number | null;
  value: string | null; currency: string | null; billing_terms: string | null; plan_tier: string | null; seats: number | null;
  notes: string | null; file: { name: string; size: number; mime_type: string } | null;
  created_by: string | null; created_at: string | null; updated_at: string | null;
  email?: string; organisation?: string | null;
  events?: { id: number; kind: string; actor: string | null; detail: Record<string, unknown> | null; created_at: string | null }[];
}

export interface ContractInput {
  user_id: number; title: string; reference?: string; contract_type: ContractType; counterparty?: string;
  start_date?: string; end_date?: string; auto_renew: boolean; renewal_term_months?: number; notice_period_days?: number;
  value?: string; currency?: string; billing_terms?: string; plan_tier?: string; seats?: number; notes?: string;
}

export const myContracts = () => apiRequest<{ contracts: Contract[] }>('/api/v1/billing/contracts', opts());
export const adminListContracts = (p: { email?: string; status?: string; ending_within_days?: number } = {}) => {
  const q = new URLSearchParams();
  if (p.email) q.set('email', p.email);
  if (p.status) q.set('status', p.status);
  if (p.ending_within_days != null) q.set('ending_within_days', String(p.ending_within_days));
  const s = q.toString();
  return apiRequest<{ contracts: Contract[]; types: ContractType[] }>(`/api/v1/admin/contracts${s ? `?${s}` : ''}`, opts());
};
export const adminGetContract = (id: number) => apiRequest<Contract>(`/api/v1/admin/contracts/${id}`, opts());
export const adminCreateContract = (p: ContractInput) => apiRequest<Contract>('/api/v1/admin/contracts', opts({ method: 'POST', body: JSON.stringify(p) }));
export const adminUpdateContract = (id: number, p: Partial<ContractInput> & { status?: 'active' | 'terminated' }) =>
  apiRequest<Contract>(`/api/v1/admin/contracts/${id}`, opts({ method: 'PATCH', body: JSON.stringify(p) }));
export const adminDeleteContract = (id: number) => apiRequest<void>(`/api/v1/admin/contracts/${id}`, opts({ method: 'DELETE' }));
export async function adminAttachContractFile(id: number, file: File): Promise<Contract> {
  // Plain fetch: apiRequest forces a JSON content-type, which breaks multipart.
  const fd = new FormData(); fd.append('file', file);
  const token = localStorage.getItem(TOKEN_KEY);
  const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  const res = await fetch(`${apiUrl}/api/v1/admin/contracts/${id}/file`, { method: 'POST', body: fd, headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let detail = `Upload failed (${res.status})`;
    try { detail = (await res.json()).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json();
}

export async function downloadContractFile(id: number, admin = false): Promise<void> {
  const apiUrl = process.env.REACT_APP_API_BASE_URL ?? '';
  const res = await fetch(`${apiUrl}${admin ? `/api/v1/admin/contracts/${id}/file` : `/api/v1/billing/contracts/${id}/file`}`, opts());
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const m = /filename="?([^"]+)"?/.exec(res.headers.get('content-disposition') || '');
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a'); a.href = href; a.download = m ? m[1] : `contract-${id}.pdf`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(href);
}

export const STATUS_COLOR: Record<ContractStatus, { bg: string; fg: string }> = {
  draft: { bg: '#f2f4f7', fg: '#344054' }, active: { bg: '#ecfdf3', fg: '#027a48' },
  expired: { bg: '#fffaeb', fg: '#b54708' }, terminated: { bg: '#fef3f2', fg: '#b42318' },
};
